import type { Inputs, Projection, ResolvedStage, YearRow } from './types';

/** Guards against an absurd horizon producing a giant array. */
export const MAX_YEARS = 100;

/**
 * Projects a pension pot forward, one year at a time.
 *
 * Contributions are paid at the *start* of each year, so each one earns that
 * year's growth. This is the more optimistic of the two conventions and is
 * called out in the UI.
 */
export function project(input: Inputs): Projection {
  const years = clampYears(input.retirementAge - input.currentAge);
  const g = input.growthRate;
  const i = input.inflationRate;
  const pot0 = safe(input.currentPot);
  const stages = resolveStages(input, years);

  const rows: YearRow[] = [];
  let balance = pot0;
  let totalContributed = 0;

  for (let t = 0; t < years; t++) {
    const age = input.currentAge + t;
    const stageIndex = stageIndexAt(stages, age);
    const stage = stages[stageIndex]!;
    const annual = safe(stage.annualContribution);
    const contribution = stage.growsWithInflation ? annual * Math.pow(1 + i, t) : annual;

    rows.push({
      yearIndex: t,
      age,
      contribution,
      stageIndex,
      balanceNominal: balance,
      balanceReal: balance / Math.pow(1 + i, t),
    });

    balance = (balance + contribution) * (1 + g);
    totalContributed += contribution;
  }

  // Final row: the pot at retirement, with nothing left to pay in.
  rows.push({
    yearIndex: years,
    age: input.currentAge + years,
    contribution: 0,
    stageIndex: stages.length - 1,
    balanceNominal: balance,
    balanceReal: balance / Math.pow(1 + i, years),
  });

  const potNominal = balance;
  const potReal = potNominal / Math.pow(1 + i, years);
  const w = safe(input.withdrawalRate);

  return {
    rows,
    years,
    stages,
    potNominal,
    potReal,
    incomeNominal: potNominal * w,
    incomeReal: potReal * w,
    totalContributed,
    totalGrowth: potNominal - pot0 - totalContributed,
    realGrowthRate: (1 + g) / (1 + i) - 1,
  };
}

/**
 * Turns the 3 input stages into concrete, non-overlapping age ranges.
 *
 * The last two stages are anchored to retirement and claim their years from
 * the end backwards — "the final 3 years" is something you can actually
 * state, whereas a span measured from today moves every birthday. Stage 1
 * then absorbs whatever's left at the front, so the default assumption is
 * that you're building right up until you say otherwise.
 */
export function resolveStages(
  input: Inputs,
  years: number = clampYears(input.retirementAge - input.currentAge),
): ResolvedStage[] {
  const y3 = clampStageYears(input.stages[2].years, years);
  const y2 = clampStageYears(input.stages[1].years, years - y3);
  const y1 = years - y3 - y2;
  const lengths = [y1, y2, y3];

  let cursor = input.currentAge;
  return input.stages.map((stage, index) => {
    const start = cursor;
    cursor += lengths[index]!;
    return {
      start,
      end: cursor,
      annualContribution: stage.annualContribution,
      growsWithInflation: stage.growsWithInflation,
    };
  });
}

function stageIndexAt(stages: ResolvedStage[], age: number): number {
  const index = stages.findIndex((s) => age >= s.start && age < s.end);
  return index === -1 ? stages.length - 1 : index;
}

function clampStageYears(raw: number, max: number): number {
  const bound = Math.max(0, max);
  return Math.min(bound, Math.max(0, Math.round(safe(raw))));
}

function clampYears(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  return Math.min(MAX_YEARS, Math.max(0, Math.round(raw)));
}

function safe(n: number): number {
  return Number.isFinite(n) ? n : 0;
}
